import { LEVIER_SOLID_CLASSES, levierColorTokenFor } from '@/lib/display-tweaks';
import { cn } from '@/lib/utils';
import {
  type Noyau,
  SETUP_IMPORT_COLUMNS,
  type SetupImportField,
  type SetupImportPreview,
  type SetupImportSkip,
  type SetupImportWrite,
} from '@mss_creator/shared';

export interface SetupImportPreviewTableProps {
  preview: SetupImportPreview;
  noyau: Noyau;
}

const FIELD_COLUMNS = SETUP_IMPORT_COLUMNS.flatMap((column) =>
  column.key === 'step' ? [] : [{ key: column.key, label: column.canonical }],
);
const REQUIRED_FIELDS: ReadonlySet<SetupImportField> = new Set(['driver', 'levierValeur']);

const WRITTEN_CELL = 'bg-bleu-ciel-tint shadow-[inset_3px_0_0_var(--color-bleu-ciel)]'; // pms-design-exception: liseré inséré des cellules écrites ou ignorées de l’aperçu d’import
const SKIPPED_CELL = 'bg-jaune-tint shadow-[inset_3px_0_0_var(--color-jaune-deep)]'; // pms-design-exception: liseré inséré des cellules écrites ou ignorées de l’aperçu d’import

function cellKey(order: number, field: SetupImportField): string {
  return `${order}:${field}`;
}

/**
 * The draft as it will read after « Appliquer » (board 03): bleu = will be
 * written (previous value struck through), jaune = not imported (too long),
 * white = kept as is. Step dots follow the draft's noyau, like the editor.
 */
export function SetupImportPreviewTable({ preview, noyau }: SetupImportPreviewTableProps) {
  const written = new Map<string, SetupImportWrite>(
    preview.written.map((write) => [cellKey(write.order, write.field), write]),
  );
  const skipped = new Map<string, SetupImportSkip>(
    preview.skippedTooLong.map((skip) => [cellKey(skip.order, skip.field), skip]),
  );
  const missingSteps = new Set(preview.missingSteps);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-separate border-spacing-0 text-small">
          <thead>
            <tr>
              <th className="w-20 px-2 py-2 text-left font-semibold text-caption text-gray-500">
                Étape
              </th>
              {FIELD_COLUMNS.map(({ key, label }) => (
                <th
                  key={key}
                  className="px-2 py-2 text-left font-semibold text-caption text-gray-500"
                >
                  {label}
                  {REQUIRED_FIELDS.has(key) && <span className="ml-0 text-danger">*</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.merged.map((levier) => {
              const absent = missingSteps.has(levier.order);
              const stepColor = levierColorTokenFor(noyau, levier.order);
              return (
                <tr key={levier.order} data-testid={`setup-import-row-${levier.order}`}>
                  <td className="whitespace-nowrap border-gray-100 border-t-2 px-2 py-2 align-top font-semibold text-gray-500">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mr-2 inline-block h-2 w-2 rounded-full align-middle',
                        LEVIER_SOLID_CLASSES[stepColor],
                      )}
                    />
                    Étape {levier.order}
                  </td>
                  {FIELD_COLUMNS.map(({ key }) => {
                    const value = levier[key];
                    const write = written.get(cellKey(levier.order, key));
                    const skip = skipped.get(cellKey(levier.order, key));
                    const base = 'border-gray-100 border-t-2 px-2 py-2 align-top';
                    if (write) {
                      return (
                        <td key={key} data-state="written" className={cn(base, WRITTEN_CELL)}>
                          {write.next}
                          {write.previous && (
                            <span className="mt-1 block text-caption text-gray-500 line-through">
                              {write.previous}
                            </span>
                          )}
                        </td>
                      );
                    }
                    if (skip) {
                      return (
                        <td key={key} data-state="skipped" className={cn(base, SKIPPED_CELL)}>
                          {value ? value : <span className="text-gray-400">-</span>}
                          <span className="mt-1 block font-semibold text-caption text-jaune-deep">
                            Non importé : {skip.length} / {skip.limit} car.
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={key}
                        data-state="kept"
                        className={cn(
                          base,
                          !value && 'text-gray-400',
                          absent && 'text-gray-400 italic',
                        )}
                      >
                        {value ? value : absent ? 'absente de l’import' : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-caption text-gray-600">
        <span>
          <span
            aria-hidden="true"
            className={cn('mr-1 inline-block h-3 w-3 rounded-sm align-[-2px]', WRITTEN_CELL)}
          />
          sera écrit
        </span>
        <span>
          <span
            aria-hidden="true"
            className={cn('mr-1 inline-block h-3 w-3 rounded-sm align-[-2px]', SKIPPED_CELL)}
          />
          non importé
        </span>
        <span>
          <span
            aria-hidden="true"
            className="mr-1 inline-block h-3 w-3 rounded-sm border border-gray-200 bg-white align-[-2px]"
          />
          conservé
        </span>
      </div>
    </>
  );
}
